const biApi = require('services/api/beautyInsider');
const profileApi = require('services/api/profile');
const { getGroupedBrandsList } = require('services/api/beautyPreferences');

const actions = require('Actions');
const store = require('Store');
const ProfileActions = require('actions/ProfileActions');
const anaConsts = require('analytics/constants');
const processEvent = require('analytics/processEvent');
const { UPDATE_BEAUTY_PREFERENCE } = require('constants/actionTypes/user');
const Storage = require('utils/localStorage/Storage');
const LOCAL_STORAGE = require('utils/localStorage/Constants');
const {
    SET_COLOR_IQ,
    SET_INITIAL_BEAUTY_PREFERENCES,
    SET_MULTIPLE_BEAUTY_PREFERENCES,
    CLEAR_BEAUTY_PREFERENCE,
    SET_EXPANDED_PREFERENCE,
    SET_PROFILE_COMPLETION_PERCENTAGE,
    SET_MAPPED_BRANDS_LIST
} = require('constants/actionTypes/beautyPreferences');
const { PREFERENCE_TYPES, profileCategories } = require('constants/beautyPreferences');
const {
    calculateProfileCompletionStatus,
    mapNotSureNoPreferenceWithBIValues,
    mapNotSureNoPreferenceToStateValues,
    mapFetchedBrandsList
} = require('utils/BeautyPreferences');

function setColorIQ(colorIQ) {
    return {
        type: SET_COLOR_IQ,
        payload: { colorIQ }
    };
}

function setInitialBeautyPreferences(initialData) {
    const initialDataWithStateValues = mapNotSureNoPreferenceToStateValues(initialData);
    const profileCompletionPercentage = calculateProfileCompletionStatus(initialDataWithStateValues);

    return {
        type: SET_INITIAL_BEAUTY_PREFERENCES,
        initialData: initialDataWithStateValues,
        profileCompletionPercentage
    };
}

function setMultipleBeautyPreferences(category, selectedProfiles) {
    return {
        type: SET_MULTIPLE_BEAUTY_PREFERENCES,
        category,
        selectedProfiles
    };
}

function clearBeautyPreference(category) {
    return {
        type: CLEAR_BEAUTY_PREFERENCE,
        category
    };
}

function setExpandedPreference(expandedPreference) {
    return {
        type: SET_EXPANDED_PREFERENCE,
        expandedPreference
    };
}

function setProfileCompletionPercentage(beautyPreferences) {
    const profileCompletionPercentage = calculateProfileCompletionStatus(beautyPreferences);

    return {
        type: SET_PROFILE_COMPLETION_PERCENTAGE,
        profileCompletionPercentage
    };
}

function updateBeautyPreferences(
    category,
    profileId,
    profileData,
    beautyPreferences = {},
    successCallback,
    errorTitle,
    errorMessage,
    errorButtonText,
    errorCallback = null
) {
    const isColorIQCategory = category.type === PREFERENCE_TYPES.COLOR_IQ;
    const colorIQForAPI = profileData;
    const colorIQForLS = beautyPreferences[PREFERENCE_TYPES.COLOR_IQ];

    const updatedBeautyPreferences = {
        ...beautyPreferences,
        ...(!isColorIQCategory && { [category.type]: profileData }),
        ...(isColorIQCategory && { [category.type]: colorIQForLS })
    };

    const personalizedInformation = mapNotSureNoPreferenceWithBIValues(updatedBeautyPreferences);

    // the API accepts colorIQ category as an object
    if (isColorIQCategory) {
        personalizedInformation[PREFERENCE_TYPES.COLOR_IQ] = colorIQForAPI;
    }

    // if there are no changes in colorIQ category we send nothing as the payload
    if (!isColorIQCategory && personalizedInformation[PREFERENCE_TYPES.COLOR_IQ]) {
        personalizedInformation[PREFERENCE_TYPES.COLOR_IQ] = {};
    }

    // WORKAROUND: delete favoriteBrands category as it's not accepted in the payload
    // shoud be fixed on https://jira.sephora.com/browse/INFL-1157 when resolved
    if (personalizedInformation[PREFERENCE_TYPES.FAVORITE_BRANDS]) {
        delete personalizedInformation[PREFERENCE_TYPES.FAVORITE_BRANDS];
    }

    const data = {
        biAccount: {
            personalizedInformation
        },
        profileId
    };

    return dispatch =>
        profileApi
            .updateBeautyPreferences(data)
            .then(response => {
                if (response.responseStatus === 200) {
                    dispatch(
                        biApi
                            .getBiProfile(profileId)
                            .then(() => {
                                const userData = Storage.local.getItem(LOCAL_STORAGE.USER_DATA, false, false, true);

                                if (userData?.data?.beautyPreference) {
                                    userData.data.beautyPreference = updatedBeautyPreferences;
                                    Storage.local.setItem(LOCAL_STORAGE.USER_DATA, userData.data, userData.expiry);
                                }

                                !isColorIQCategory && dispatch(setMultipleBeautyPreferences(category.type, profileData));
                                dispatch(setProfileCompletionPercentage(updatedBeautyPreferences));
                                successCallback && successCallback();
                            })
                            .catch(() => errorCallback && errorCallback())
                    );
                }
            })
            .catch(error => {
                if (error instanceof TypeError && error.message.includes('Network')) {
                    const closeAPIErrorModal = () => dispatch(actions.showInfoModal({ isOpen: false }));

                    dispatch(
                        actions.showInfoModal({
                            isOpen: true,
                            title: errorTitle,
                            message: errorMessage,
                            buttonText: errorButtonText,
                            callback: errorCallback ? errorCallback : closeAPIErrorModal,
                            showCancelButton: false,
                            footerColumns: 1,
                            buttonWidth: [164, 126],
                            footerDisplay: 'flex',
                            footerJustifyContent: 'flex-end',
                            bodyFooterPaddingX: 4,
                            isHtml: false,
                            cancelCallback: closeAPIErrorModal
                        })
                    );
                }

                if (error && error?.errorCode) {
                    errorCallback && errorCallback();
                }
            });
}

const updateBeautyPreference = data => ({
    type: UPDATE_BEAUTY_PREFERENCE,
    payload: data
});

function fireAnalytics(type, isLastPref, isAccordionClick, buttonType, nextCatType) {
    const { BEAUTY_PREFERENCES, MY_SEPHORA } = anaConsts.PAGE_NAMES;
    const actionInfo = {
        skipButton: `${MY_SEPHORA}:skip:${type}`,
        saveButton: isAccordionClick ? '' : `${MY_SEPHORA}:${isLastPref ? 'save' : 'save&continue'}:${type}`
    };

    const processType = isAccordionClick ? anaConsts.ASYNC_PAGE_LOAD : anaConsts.LINK_TRACKING_EVENT;
    const categoryName = profileCategories.find(item => item.type === nextCatType)?.name;
    const pageDetail = isAccordionClick || nextCatType ? 'accordion' : BEAUTY_PREFERENCES;

    const data = {
        pageName: `${MY_SEPHORA}:accordion:${nextCatType ? categoryName : type}:*`,
        actionInfo: actionInfo[buttonType],
        linkName: 'D=c55',
        pageType: MY_SEPHORA,
        pageDetail,
        world: nextCatType ? categoryName : type
    };

    if (buttonType && !isAccordionClick) {
        const pageName = `${MY_SEPHORA}:accordion:${type}:*`;
        digitalData.page.attributes.previousPageData.linkData = actionInfo[buttonType];
        digitalData.page.attributes.sephoraPageInfo.pageName = pageName;

        processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
            data
        });
    }

    if (isAccordionClick) {
        return processEvent.process(processType, {
            data: {
                ...data,
                pageName: `${MY_SEPHORA}:accordion:${type}:*`,
                world: type
            }
        });
    } else {
        return null;
    }
}

function fireAnalyticsDataOverwrites(overwrites = {}, isPageLoad = false) {
    // with isPageLoad=true fires s.t call and isPageLoad=false fires s.tl call
    const processType = isPageLoad ? anaConsts.ASYNC_PAGE_LOAD : anaConsts.LINK_TRACKING_EVENT;

    return processEvent.process(processType, {
        data: overwrites
    });
}

const openShadeFinderModal = () => actions.showWizard(true, undefined);

const openRegisterBIModal = (callback = null) => actions.showBiRegisterModal({ isOpen: true, callback, cancellationCallback: callback });

const openPrivacySettingsModal = (title, beautyPrefs, socialProfile, saveProfileCallback) =>
    ProfileActions.showEditFlowModal(true, title, 2, beautyPrefs, socialProfile, saveProfileCallback);

const closePrivacySettingsModal = (profileData, callback) => {
    store.dispatch(ProfileActions.updateBiAccount(profileData.biPrivate, callback));
};

function setMappedBrandsList(brandsList) {
    const mappedBrandsList = mapFetchedBrandsList(brandsList);

    return {
        type: SET_MAPPED_BRANDS_LIST,
        payload: { mappedBrandsList }
    };
}

function fetchGroupedBrandsList() {
    return dispatch =>
        getGroupedBrandsList()
            .then(data => (data.groupedBrands ? dispatch(setMappedBrandsList(data.groupedBrands)) : null))
            .catch(() => {});
}

module.exports = {
    updateBeautyPreferences,
    clearBeautyPreference,
    setMultipleBeautyPreferences,
    setColorIQ,
    fireAnalytics,
    fireAnalyticsDataOverwrites,
    openShadeFinderModal,
    updateBeautyPreference,
    setExpandedPreference,
    setInitialBeautyPreferences,
    openRegisterBIModal,
    openPrivacySettingsModal,
    closePrivacySettingsModal,
    setProfileCompletionPercentage,
    fetchGroupedBrandsList
};
